import React, { useState, useEffect, useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import LoadingOverlay from "../0. Utilidades/LoadingOverlay";
import { SiMicrosoftexcel } from "react-icons/si";
import { Modal, Button, Badge } from "react-bootstrap";
import "./procesos.css";

function ProcesoList() {
  const [procesos, setProcesos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCurso, setSelectedCurso] = useState("");
  const [selectedPostulante, setSelectedPostulante] = useState(null);

  useEffect(() => {
    const apiUrl = `${process.env.REACT_APP_BASE_API}api/seleccion/postulantes_with_details/`;
    fetch(apiUrl)
      .then((response) => response.json())
      .then((data) => {
        setProcesos(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      });
  }, []);

  const cursos = useMemo(() => {
    return [...new Set(procesos.map((proceso) => proceso.comuna))];
  }, [procesos]);

  const filteredProcesos = useMemo(() => {
    if (!selectedCurso) return procesos;
    return procesos.filter((proceso) => proceso.comuna === selectedCurso);
  }, [procesos, selectedCurso]);

  return (
    <div id="proceso-list">
      <h3>
        Historial de Postulaciones
        <a
          href={`${process.env.REACT_APP_BASE_API}api/seleccion/descargar_postulantes/`}
          rel="noopener noreferrer"
        >
          <SiMicrosoftexcel style={{ marginLeft: "10px", cursor: "pointer" }} />
        </a>
      </h3>
      <h6>
        Esta sección muestra las mujeres que han postulado a programas que aún
        están activos.
      </h6>

      <div className="mb-3">
        <label htmlFor="curso-select">Seleccionar Comuna:</label>
        <select
          id="curso-select"
          className="form-control"
          value={selectedCurso}
          onChange={(e) => setSelectedCurso(e.target.value)}
        >
          <option value="">Todas las Comunas</option>
          {cursos.map((curso, index) => (
            <option key={index} value={curso}>
              {curso}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <LoadingOverlay />}
      {filteredProcesos.map((proceso) => (
        <ProcesoSection
          key={proceso.id}
          proceso={proceso}
          setSelectedPostulante={setSelectedPostulante}
        />
      ))}

      <PostulanteModal
        postulante={selectedPostulante}
        handleClose={() => setSelectedPostulante(null)}
      />
    </div>
  );
}

function ProcesoSection({ proceso, setSelectedPostulante }) {
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const removeDiacritics = (text) => {
    if (text == null) {
      return "";
    }
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  const normalizedSearchQuery = removeDiacritics(searchQuery.toLowerCase());

  const filteredPostulantes = useMemo(() => {
    return proceso.postulantes.filter((postulante) => {
      const fullName = removeDiacritics(
        `${postulante.nombre} ${postulante.apellido}`.toLowerCase(),
      );
      const rut = removeDiacritics(postulante.rut?.toLowerCase());

      return (
        fullName.includes(normalizedSearchQuery) ||
        rut.includes(normalizedSearchQuery)
      );
    });
  }, [proceso.postulantes, normalizedSearchQuery]);

  const data = useMemo(() => filteredPostulantes, [filteredPostulantes]);
  const columns = useMemo(
    () => [
      {
        Header: "Postulación",
        accessor: "fecha_ingreso",
        Cell: ({ row }) => <div>{row.original.fecha_ingreso}</div>,
        id: "fecha_ingreso",
      },
      {
        Header: "Nombre",
        accessor: "nombre",
        Cell: ({ row }) => (
          <div>
            {row.original.nombre} {row.original.apellido}
          </div>
        ),
        id: "nombre",
      },
      {
        Header: "Estado",
        accessor: "estado",
        Cell: ({ row }) => (
          <Badge bg={row.original.estado === "activa" ? "success" : "danger"}>
            {row.original.estado}
          </Badge>
        ),
        id: "estado",
      },
    ],
    [],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 5 },
    },
    useSortBy,
    usePagination,
  );

  return (
    <div className="proceso-section">
      <h3>
        {proceso.nombre} ({proceso.comuna})
      </h3>
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Buscar..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>
      <div className="pagination">
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          {[5, 10, 20].map((size) => (
            <option key={size} value={size}>
              Mostrar {size}
            </option>
          ))}
        </select>
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {"<"}
        </button>
        <span>
          Página {pageIndex + 1} de {pageOptions.length}
        </span>
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          {">"}
        </button>
      </div>
      <table {...getTableProps()} className="table table-hover">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render("Header")}
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? " 🔽"
                        : " 🔼"
                      : ""}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr
                style={{ cursor: "pointer" }}
                {...row.getRowProps()}
                onClick={() => setSelectedPostulante(row.original)}
              >
                {row.cells.map((cell) => {
                  return (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PostulanteModal({ postulante, handleClose }) {
  if (!postulante) return null;

  return (
    <Modal show={true} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Detalles de la Postulante</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          <strong>Fecha de Ingreso:</strong> {postulante.fecha_ingreso}
        </p>
        <p>
          <strong>Estado:</strong>{" "}
          <Badge bg={postulante.estado === "activa" ? "success" : "danger"}>
            {postulante.estado}
          </Badge>
        </p>

        <hr />
        <p>
          <strong>RUT:</strong> {postulante.rut}
        </p>
        <p>
          <strong>Nombre:</strong> {postulante.nombre} {postulante.apellido}
        </p>
        <p>
          <strong>Comuna:</strong> {postulante.comuna}
        </p>

        <p>
          <strong>Celular:</strong>
          <a href={`tel:${postulante.celular}`}> +56{postulante.celular}</a>
        </p>
        <p style={{ overflow: "hidden" }}>
          <strong>Correo:</strong>
          <a href={`mailto:${postulante.correo}`}> {postulante.correo}</a>
        </p>
        <hr />
        <p>
          <strong>Entrevista Resultado:</strong>{" "}
          {postulante.entrevista_resultado}
        </p>
        <p>
          <strong>Entrevista Estado:</strong>{" "}
          <Badge
            bg={
              postulante.entrevista_estado === "aprobada" ? "success" : "danger"
            }
          >
            {postulante.entrevista_estado}
          </Badge>
        </p>
        <hr />
        <p>
          <strong>Psicoanálisis Resultado:</strong>{" "}
          {postulante.psicoanalisis_resultado}
        </p>
        <p>
          <strong>Psicoanálisis Estado:</strong>{" "}
          <Badge
            bg={
              postulante.psicoanalisis_estado === "aprobada"
                ? "success"
                : "danger"
            }
          >
            {postulante.psicoanalisis_estado}
          </Badge>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ProcesoList;
